import React, { useEffect, useState } from 'react';
import GoogleLogo from './GoogleLogo';
import StarRating from './StarRating';
import {
  fetchGoogleReviews,
  formatReviewCount,
  getAuthorInitials,
  getRatingLabel,
  getReviewRelativeTime,
} from '../utils/googleReviewsApi';

function ReviewAvatar({ author, photoUrl }) {
  const [failed, setFailed] = useState(false);

  if (photoUrl && !failed) {
    return (
      <img
        className="tsr-greviews-widget__avatar-img"
        src={photoUrl}
        alt={author}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }

  return <span className="tsr-greviews-widget__avatar-initials">{getAuthorInitials(author)}</span>;
}

function GoogleReviewsBadgeWidget({
  data: dataProp,
  variant = 'inline',
  showDismiss = false,
  onDismiss,
}) {
  const [data, setData] = useState(dataProp || null);
  const [loading, setLoading] = useState(!dataProp);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (dataProp) {
      setData(dataProp);
      setLoading(false);
      return undefined;
    }

    let active = true;
    fetchGoogleReviews()
      .then((result) => {
        if (active) setData(result);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [dataProp]);

  if (loading || !data) {
    return null;
  }

  const reviews = data.reviews || [];
  const activeReview = reviews[activeIndex] || reviews[0];
  const activeReviewTime = activeReview ? getReviewRelativeTime(activeReview) : '';
  const hasMultiple = reviews.length > 1;
  const reviewUrl = data.googleMapsUrl;

  const goPrev = () => {
    setActiveIndex((index) => (index - 1 + reviews.length) % reviews.length);
  };

  const goNext = () => {
    setActiveIndex((index) => (index + 1) % reviews.length);
  };

  return (
    <div className={`tsr-greviews-widget tsr-greviews-widget--${variant}`}>
      {showDismiss ? (
        <button
          type="button"
          className="tsr-greviews-widget__close"
          onClick={onDismiss}
          aria-label="Close Google reviews"
        >
          &times;
        </button>
      ) : null}

      <div className="tsr-greviews-widget__head">
        <GoogleLogo className="tsr-greviews-widget__logo" />
        <div className="tsr-greviews-widget__head-text">
          <div className="tsr-greviews-widget__title">{getRatingLabel(data.rating)}</div>
          <div className="tsr-greviews-widget__summary">
            <i className="fas fa-star tsr-greviews-widget__summary-star" aria-hidden="true" />
            <span>
              {data.rating.toFixed(1)} out of 5 based on {formatReviewCount(data.reviewCount)} reviews
            </span>
          </div>
        </div>
      </div>

      {reviewUrl ? (
        <a
          className="tsr-greviews-widget__cta"
          href={reviewUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Review us on Google
        </a>
      ) : null}

      <div className="tsr-greviews-widget__divider" />

      {activeReview ? (
        <div className="tsr-greviews-widget__review">
          <div className="tsr-greviews-widget__reviewer">
            <div className="tsr-greviews-widget__avatar-wrap">
              <ReviewAvatar author={activeReview.author} photoUrl={activeReview.profilePhotoUrl} />
              <span className="tsr-greviews-widget__avatar-badge" aria-hidden="true">
                <GoogleLogo />
              </span>
            </div>
            <div className="tsr-greviews-widget__reviewer-meta">
              <div className="tsr-greviews-widget__author-row">
                <span className="tsr-greviews-widget__author">{activeReview.author}</span>
                <svg
                  className="tsr-greviews-widget__verified"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  aria-label="Verified"
                >
                  <path
                    fill="#1a73e8"
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.25 14.5L6.5 12.25l1.41-1.41 2.84 2.84 6.34-6.34 1.41 1.41-7.75 7.75z"
                  />
                </svg>
              </div>
              {activeReviewTime ? (
                <span className="tsr-greviews-widget__time">{activeReviewTime}</span>
              ) : null}
            </div>
          </div>

          <StarRating rating={activeReview.rating} size="sm" className="tsr-greviews-widget__stars" />

          {activeReview.text ? (
            <p className="tsr-greviews-widget__text">{activeReview.text}</p>
          ) : null}
        </div>
      ) : null}

      {hasMultiple ? (
        <div className="tsr-greviews-widget__nav">
          <button type="button" className="tsr-greviews-widget__nav-btn" onClick={goPrev} aria-label="Previous review">
            &#8249;
          </button>
          <div className="tsr-greviews-widget__dots">
            {reviews.map((review, index) => (
              <button
                key={`${review.author}-${index}`}
                type="button"
                className={`tsr-greviews-widget__dot ${
                  index === activeIndex ? 'tsr-greviews-widget__dot--active' : ''
                }`}
                onClick={() => setActiveIndex(index)}
                aria-label={`Show review ${index + 1}`}
              />
            ))}
          </div>
          <button type="button" className="tsr-greviews-widget__nav-btn" onClick={goNext} aria-label="Next review">
            &#8250;
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default GoogleReviewsBadgeWidget;
